import {
	concatWith,
	defer,
	EMPTY,
	exhaustMap,
	filter,
	finalize,
	fromEvent,
	map,
	merge,
	of,
	take,
	takeUntil,
	tap,
	type Observable,
} from 'rxjs'

/** Below this movement (px) a press is a tap, and no session ever opens. */
export const POINTER_SESSION_THRESHOLD_PX = 6

/**
 * `opened` is the first move past the threshold, `moved` every one after it.
 * `committed` is `pointerup` and `discarded` is `pointercancel` — a gesture the
 * OS took away (a system swipe, an incoming call) expressed no intent to drop.
 * A press that never passed the threshold emits none of them: it was a tap.
 */
export type PointerSessionPhase = 'opened' | 'moved' | 'committed' | 'discarded'

export interface PointerSessionStep {
	phase: PointerSessionPhase
	/** The pointer event that produced this step — the move, the up, or the cancel. */
	event: PointerEvent
}

export interface PointerSessionOptions {
	/** Movement in px before a press becomes a session. */
	threshold?: number
	/** Read at each `pointerdown`: a press this refuses opens nothing, without the element becoming inert. */
	enabled?: () => boolean
	/** Read against the down event's composed path before the session owns it. */
	starts?: (event: PointerEvent) => boolean
}

/**
 * One press-drag-release session per element, as an Observable.
 *
 * The mechanics every pointer gesture in this library needs and none should
 * re-derive: `setPointerCapture` so the session survives the pointer leaving
 * the element, a movement threshold so a tap stays a tap, `exhaustMap` so a
 * second finger cannot restart a session already in flight, and an end that
 * listens for `pointerup` AND `pointercancel` — a session waiting only for
 * `pointerup` stays open forever on touch and stylus, where the OS ends a
 * gesture it appropriates with the latter.
 *
 * Nothing here paints, moves or announces anything: the caller decides what a
 * session means. `reorder()` keeps its own equivalent session for now; its
 * migration onto this is a named follow-up rather than part of this landing.
 */
export function pointerSession(element: HTMLElement, options: PointerSessionOptions = {}): Observable<PointerSessionStep> {
	const threshold = options.threshold ?? POINTER_SESSION_THRESHOLD_PX

	return fromEvent<PointerEvent>(element, 'pointerdown').pipe(
		filter(
			down =>
				down.isPrimary &&
				down.button === 0 &&
				(options.enabled?.() ?? true) &&
				(options.starts?.(down) ?? true),
		),
		// First-wins: a second finger landing mid-session cannot restart it.
		exhaustMap(down => {
			let opened = false
			let captured = false
			let ending: PointerEvent | null = null

			const end$ = merge(
				fromEvent<PointerEvent>(window, 'pointerup'),
				fromEvent<PointerEvent>(window, 'pointercancel'),
			).pipe(
				filter(end => end.pointerId === down.pointerId),
				take(1),
				tap(end => {
					ending = end
				}),
			)

			return fromEvent<PointerEvent>(window, 'pointermove').pipe(
				filter(move => move.pointerId === down.pointerId),
				filter(
					move => opened || Math.hypot(move.clientX - down.clientX, move.clientY - down.clientY) >= threshold,
				),
				tap(() => {
					if (opened) return
					try {
						element.setPointerCapture(down.pointerId)
						captured = true
					} catch {
						// The window listeners still own the finite session.
					}
				}),
				map((move): PointerSessionStep => {
					const step: PointerSessionStep = { phase: opened ? 'moved' : 'opened', event: move }
					opened = true
					return step
				}),
				takeUntil(end$),
				concatWith(
					defer(() =>
						opened && ending !== null
							? of<PointerSessionStep>({
									phase: ending.type === 'pointercancel' ? 'discarded' : 'committed',
									event: ending,
								})
							: EMPTY,
					),
				),
				finalize(() => {
					if (!captured || !element.hasPointerCapture(down.pointerId)) return
					try {
						element.releasePointerCapture(down.pointerId)
					} catch {
						// Native pointer release may have won the race.
					}
				}),
			)
		}),
	)
}
